import React, { useEffect, useMemo, useState } from 'react';
import { Link, Stack } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import MfResponsiveTable from '../UI/MfResponsiveTable';
import useAgentRequestList from './useAgentRequestList';
import MfButton from '../UI/Button/MfButton';
import MfDialog from '../UI/Dialog/MfDialog';
import AgentRequestForm from '../AgentRequest';
import { AgentRequestResponse } from '../../structures/response/AgentRequestResponse';
import MfAlert from '../UI/Alert/MfAlert';
import useClosableSnackbar from '../../hooks/useClosableSnackbar';
import AgentRequestView from '../AgentRequest/AgentRequestView/AgentRequestView';
import ConsultationForm from './ConsultationForm';
import useProfileReady from '../../hooks/useProfileReady';
import SignAgentReportDialog from '../Sign/SignAgentReportDialog';
import { columns } from './TableColumns';
import { getTableData } from './TableData';
import StatisticsAlert from './StatisticsAlert';
import ReferralLinkComponent from './ReferralLinkComponent';
import PledgeConfirmDialog from './TableCustomCells/PledgeConfirmDialog';

const AgentRequestList = () => {
  const [open, setOpen] = useState<boolean>(false);
  const [responseToEdit, setResponseToEdit] =
    useState<AgentRequestResponse | null>(null);
  const [responseToSign, setResponseToSign] =
    useState<AgentRequestResponse | null>(null);
  const [responseToConfirmPledge, setResponseToConfirmPledge] =
    useState<AgentRequestResponse | null>(null);
  const [responseToView, setResponseToView] =
    useState<AgentRequestResponse | null>(null);
  const [responseToCallback, setResponseToCallback] =
    useState<AgentRequestResponse | null>(null);
  const [agentRequestResponses, getData] = useAgentRequestList();
  const { showSnackbar } = useClosableSnackbar();
  const profileReady = useProfileReady();

  useEffect(() => {
    if (agentRequestResponses && responseToSign?.id) {
      const agentRequestResponse = agentRequestResponses.find(
        (v) => v.id === responseToSign?.id
      );
      if (
        agentRequestResponse &&
        agentRequestResponse?.report?.reportDocument?.isSigned !==
          responseToSign?.report?.reportDocument?.isSigned
      ) {
        setResponseToSign(agentRequestResponse);
      }
    }
  }, [responseToSign?.id, agentRequestResponses]);

  const hasSuccessRequest = useMemo(
    () => agentRequestResponses.some((v) => v.status === 'WAIT_PAYMENT'),
    [agentRequestResponses]
  );

  function handleEdit(v: AgentRequestResponse) {
    setResponseToEdit(v);
    setOpen(true);
  }

  function handleView(v: AgentRequestResponse) {
    setResponseToView(v);
  }

  function handleCallback(v: AgentRequestResponse) {
    setResponseToCallback(v);
  }

  function handleSign(v: AgentRequestResponse) {
    setResponseToSign(v);
  }

  function handleConfirmPledge(v: AgentRequestResponse) {
    setResponseToConfirmPledge(v);
  }

  return (
    <>
      <Stack direction="row" gap={1} flexWrap="wrap">
        <MfButton variant="contained" onClick={() => setOpen(true)}>
          Создать заявку
        </MfButton>
        <MfButton variant="contained" href="#/info">
          Полезные материалы
        </MfButton>
      </Stack>
      {hasSuccessRequest && !profileReady && (
        <MfAlert severity="warning">
          Для получения вознаграждения необходимо заполнить данные о себе и
          подписать документы в{' '}
          <Link component={RouterLink} to="/profile">
            профиле
          </Link>
        </MfAlert>
      )}
      <h2>Статистика по отправленным заявкам</h2>
      <div>
        <MfResponsiveTable
          columns={columns}
          data={getTableData(
            agentRequestResponses,
            handleEdit,
            handleView,
            handleCallback,
            handleSign,
            handleConfirmPledge
          )}
          options={{
            responsive: 'simple',
            pagination: true,
            rowsPerPage: 5,
            count: agentRequestResponses.length,
          }}
        />
      </div>
      <StatisticsAlert />
      <h2 style={{ marginTop: '90px' }}>
        Передать заёмщику свою реферальную ссылку
      </h2>
      <ReferralLinkComponent />
      <MfDialog
        onClose={() => setOpen(false)}
        open={open}
        title={responseToEdit?.id ? 'Изменить заявку' : 'Отправить заявку'}
        maxWidth="md"
      >
        <AgentRequestForm
          initialValues={responseToEdit}
          onSubmitted={() => {
            showSnackbar('Спасибо, заявка отправлена, мы свяжемся с вами!', {
              autoHideDuration: 10000,
            });
            setOpen(false);
            getData();
          }}
        />
      </MfDialog>
      <MfDialog
        onClose={() => setResponseToView(null)}
        open={Boolean(responseToView)}
        title={`Просмотр заявки №${responseToView?.id}`}
      >
        <AgentRequestView data={responseToView} />
      </MfDialog>
      <MfDialog
        title="Запрос консультации"
        onClose={() => setResponseToCallback(null)}
        open={Boolean(responseToCallback)}
        maxWidth="md"
        cancelButton={false}
      >
        <ConsultationForm data={responseToCallback} />
      </MfDialog>
      <SignAgentReportDialog
        open={Boolean(responseToSign)}
        onClose={() => setResponseToSign(null)}
        request={responseToSign}
      />
      <PledgeConfirmDialog
        open={Boolean(responseToConfirmPledge)}
        onClose={() => setResponseToConfirmPledge(null)}
        request={responseToConfirmPledge}
        onConfirmed={() => {
          getData();
        }}
      />
    </>
  );
};

export default AgentRequestList;
